/**
 * @Author: Li, Yu Jia
 * @Date: 2020-02-06 14:39:45
 * @Last Modified by: Li, Yu Jia
 * @Last Modified time: 2021-07-21 14:03:37
 * @Usage: imprintarea.selectors.fun
 */

import {
  IMPRINT_AREA_NONE,
  ProcessTypes,
  colorSpace,
  colorType,
  elementStatus,
  imprintOptionType,
  userImageType,
} from '../utils/constants';
import { TYPESETTING_LOCATION_LOGO, TYPESETTING_LOCATION_NONE } from './constants';
import {
  checkImprintOptionOfferLogo,
  getAllSelectedImprintAreaKeysFromImprintAreasCombinedDict,
  getIsLaserGrowType,
  getNormalImages,
  imprintColorFormat,
  layoutFormat,
  locationListSort,
  removeColorDuplicates,
} from './logics';
import {
  getAdditionalColorCharge,
  getAdditionalImprintCharge,
  getInsideCoverCharge,
  getLogoEnhancementCharge,
  getPmsColorCharge,
} from '../charges/selectors';
import { getBomColorsInfo, getPreviewColor } from '../preview/selectors';
import {
  getColorCode,
  getCurrentImprintAreaCode,
  getCurrentImprintAreaKey,
  getInitializedImprintAreaKeys,
  getStudioItemType,
  getTexturizedImageName,
} from '../main/selectors';

import { SelectionType } from '../bom/constants';
import { TemplateType } from '../template/constants';
import { bomOptionType } from '../utils/constants';
import { createSelector } from 'reselect';
import { formatCharge } from '../utils/formatter';
import { getCanvasImageList } from '../canvas/selectors';
import { getCurrentBomOption } from '../nextgen/product/selectors';
import { getMatchTrimColor } from '../bom/selectors';
import { getProductPreviews } from '../preview/selectors';
import { getTextLine } from '../text/selectors';
import { outlineThicknessOption } from '../text/constants';
import { replaceDesignPreviewUrlPlaceholder } from '../preview/selectors';
import { studioItemType } from '../main/constants';

const getImprintOptionTypeHelper = (imprintOptionTypeArr) => {
  let typeKeySelected = imprintOptionType.NA;
  if (imprintOptionTypeArr) {
    const typeSelected = imprintOptionTypeArr.find((x) => x.selected);
    if (typeSelected) {
      typeKeySelected = typeSelected.key || imprintOptionType.NA;
    }
  }
  return typeKeySelected;
};

export const getImprintAreasInit = createSelector(
  (state) => state.imprintAreaDict,
  (state) => state.imprintAreasCombinedDict,
  (imprintAreaDict, imprintAreasCombinedDict) => {
    return {
      formats: imprintAreaDict,
      groups: imprintAreasCombinedDict,
    };
  }
);

/**
 * all imprint areas key including NA-NA-NA
 */
export const getAllSelectedImprintAreaKeys = createSelector(
  (state) => state.imprintAreasCombinedDict,
  (imprintAreasCombinedDict) => {
    return getAllSelectedImprintAreaKeysFromImprintAreasCombinedDict(imprintAreasCombinedDict);
  }
);

export const getCombinationByImprintAreas = (state, groupName, imprintAreas) => {
  const group = state.imprintAreasCombinedDict[groupName];
  let currentCombination;

  if (group && group.combinations) {
    group.combinations.forEach((combination) => {
      let isCurrent = false;
      if (combination.formats && combination.formats.length > 0 && combination.formats.length === imprintAreas.length) {
        isCurrent = imprintAreas.every((imprintArea) => combination.formats.some((x) => x === imprintArea));
      }

      if (isCurrent) {
        currentCombination = combination;
      }
    });
  }

  return currentCombination;
};

export const getImprintStaticThumbnailImage = (state, imprintAreaKey) => {
  const imprintAreaDict = getAllNextGenUIImprintAreaDict(state);
  const group = imprintAreaDict[imprintAreaKey]?.group;
  if (!group) {
    return '';
  }

  const combination = getCombinationByImprintAreas(state, group, [imprintAreaKey]);
  if (!combination) {
    return '';
  }

  return combination.thumbnailFilename;
};

export const getCurrentSelectedCombination = (state, groupName) => {
  const combinedDict = state.imprintAreasCombinedDict;
  if (Object.keys(combinedDict).includes(groupName)) {
    const groupCombinations = combinedDict[groupName].combinations;
    return groupCombinations.find((x) => x.selected);
  }
};

export const getImprintAreaDetailInfo = (state, imprintAreaKey) => {
  return state.imprintAreaDict[imprintAreaKey];
};

export const getImprintAreaApplied = (state, imprintAreaKey) => {
  const customized = getImprintAreaCustomized(state, imprintAreaKey);

  if (!customized) return false;

  const imprintAreasCombinedDict = state.imprintAreasCombinedDict;
  const selectedImprintAreas = getAllSelectedImprintAreaKeysFromImprintAreasCombinedDict(imprintAreasCombinedDict);
  const selected = selectedImprintAreas.indexOf(imprintAreaKey) > -1;

  return selected;
};

export const getImprintAreaCustomizedWithoutSpecialImage = (state, imprintAreaKey) => {
  const imageDict = state.imageDict;
  const textDict = state.textDict;

  const hasImage =
    imageDict &&
    imageDict[imprintAreaKey] &&
    imageDict[imprintAreaKey].filter(
      (img) =>
        img.data.type !== userImageType.Verse &&
        img.data.type !== userImageType.Sentiment &&
        !(img.data.type === userImageType.Slogan && img.data.isOutside)
    ).length > 0;
  const hasText = textDict && textDict[imprintAreaKey] && textDict[imprintAreaKey].some((x) => x.data.imprintText);

  const finalTemplate = getFinalTemplateForImprintArea(state, imprintAreaKey);
  const hasTemplate = finalTemplate && finalTemplate.templateType !== TemplateType.BLANK;

  return hasImage || hasText || hasTemplate;
};

export const getImprintAreaCustomized = (state, imprintAreaKey) => {
  const imageDict = state.imageDict;
  const textDict = state.textDict;

  const hasImage = imageDict && imageDict[imprintAreaKey] && imageDict[imprintAreaKey].length > 0;
  const hasText = textDict && textDict[imprintAreaKey] && textDict[imprintAreaKey].some((x) => x.data.imprintText);

  const finalTemplate = getFinalTemplateForImprintArea(state, imprintAreaKey);
  const hasTemplate = finalTemplate && finalTemplate.templateType !== TemplateType.BLANK;

  return hasImage || hasText || hasTemplate;
};

export const getFinalTemplateForImprintArea = createSelector(
  (state, currentImprintKey) => currentImprintKey || getCurrentImprintAreaKey(state),
  (state) => state.specialTemplateDict,
  (state) => state.templateDict,
  (state) => state.customBackgroundDict,
  (key, specialTemplateDict, commonTemplateDict, customBackgroundDict) => {
    const specialTemplates = Object.keys(specialTemplateDict).includes(key) ? specialTemplateDict[key] : [];
    const specialTemplate = specialTemplates.find((template) => template.isSelected);

    const subCommonTemplateDict = Object.keys(commonTemplateDict).includes(key) ? commonTemplateDict[key] : {};
    const commonTemplate = Object.values(subCommonTemplateDict)
      .flat()
      .find((template) => template.isSelected);

    const customBackground = Object.keys(customBackgroundDict).includes(key) ? customBackgroundDict[key] : {};

    return specialTemplate
      ? specialTemplate.templateType === TemplateType.CUSTOM
        ? customBackground
        : specialTemplate
      : commonTemplate;
  }
);

export const getAllAppliedImprintAreaKeys = (state) => {
  const allImprintKeys = getAllSelectedImprintAreaKeys(state);

  let appliedKeys = [];
  allImprintKeys.forEach((imprintKey) => {
    const isApplied = getImprintAreaApplied(state, imprintKey);
    if (isApplied) {
      appliedKeys.push(imprintKey);
    }
  });

  return appliedKeys;
};

export const getGroupImprintsAdditionalInfo = (state, groupName) => {
  const allAppliedKeys = getAllAppliedImprintAreaKeys(state);
  const group = state.imprintAreasCombinedDict[groupName];
  const groupCombinations = group.combinations;

  let imprintAdditionalChargeDict = {};

  const groupDict = getAllGroupImprintAreas(state);

  const groupImprintAreas = groupDict[groupName];
  const groupAppliedKeys = groupImprintAreas.filter((f) => allAppliedKeys.indexOf(f) > -1);
  const groupApplied = groupAppliedKeys && groupAppliedKeys.length > 0;

  const currentImprintAreaKey = getCurrentImprintAreaKey(state);
  const currentInGroup = groupImprintAreas.indexOf(currentImprintAreaKey) > -1;
  let freeKey = null;

  if (!groupApplied) {
    if (currentInGroup) {
      freeKey = currentImprintAreaKey;
    } else {
      freeKey = groupImprintAreas[0];
    }
  } else {
    freeKey = groupAppliedKeys[0];
  }

  groupImprintAreas.forEach((key, index) => {
    const specilImprintChargeCharge = getAddtionalImprintSpecialCharge(state, key);
    let additionalImprintCharge = 0;
    if (key !== freeKey) {
      const combinationKey = [key, freeKey];
      const canCombinedWithFreeKey = groupCombinations.find((f) =>
        combinationKey.every((c) => f.formats.indexOf(c) > -1)
      );
      if (canCombinedWithFreeKey) {
        additionalImprintCharge = getAdditionlEachImprintCharge(state);
      }
    }

    const totalImprintCharge = specilImprintChargeCharge + additionalImprintCharge;

    const result = {
      imprintAreaApplied: groupAppliedKeys.indexOf(key) > -1,
      hasAdditionalChargeMoney: totalImprintCharge > 0,
      additionalCharge: totalImprintCharge > 0 ? formatCharge(totalImprintCharge) : false,
    };
    imprintAdditionalChargeDict[key] = result;
  });

  return imprintAdditionalChargeDict;
};

export const getAddtionalImprintSpecialCharge = (state, imprintKey) => {
  let _charge = 0;
  const imprintAreaDict = state.imprintAreaDict;

  if (imprintAreaDict[imprintKey].isInsideCover) {
    const insideCover = getInsideCoverCharge(state);
    _charge += insideCover.hasCharge && !insideCover.isNotAvailable ? insideCover.chargeAmount : 0;
  }

  if (imprintAreaDict[imprintKey].isSuperSized) {
    const logoEnhancement = getLogoEnhancementCharge(state);
    _charge += logoEnhancement.hasCharge && !logoEnhancement.isNotAvailable ? logoEnhancement.chargeAmount : 0;
  }

  return _charge;
};

export const getAdditionlEachImprintCharge = (state) => {
  const additionalImprint = getAdditionalImprintCharge(state, 2);

  const additionImprintCharge =
    additionalImprint.hasCharge && !additionalImprint.isNotAvailable ? additionalImprint.chargeAmount : 0;
  return additionImprintCharge;
};

export const getImprintAdditionalCharge = (state, imprintAreaKey) => {
  let _charge = 0;
  const imprintAreaDict = state.imprintAreaDict;

  if (imprintAreaDict[imprintAreaKey].isInsideCover) {
    const insideCover = getInsideCoverCharge(state);
    _charge += insideCover.hasCharge && !insideCover.isNotAvailable ? insideCover.chargeAmount : 0;
  }

  if (imprintAreaDict[imprintAreaKey].isSuperSized) {
    const logoEnhancement = getLogoEnhancementCharge(state);
    _charge += logoEnhancement.hasCharge && !logoEnhancement.isNotAvailable ? logoEnhancement.chargeAmount : 0;
  }

  const allAppliedKeys = getAllAppliedImprintAreaKeys(state);

  const currentImprintAreaDict = getAllNextGenUIImprintAreaDict(state);
  const group = currentImprintAreaDict[imprintAreaKey].group;
  const groupDict = getAllGroupImprintAreas(state);
  const groupKeys = groupDict[group];

  let appliedKeyInOrder = groupKeys.filter((x) => allAppliedKeys.indexOf(x) > -1);

  if (appliedKeyInOrder.length > 1) {
    const additionalImprint = getAdditionalImprintCharge(state, allAppliedKeys.length);

    const totalAdditionalCharge =
      additionalImprint.hasCharge && !additionalImprint.isNotAvailable ? additionalImprint.chargeAmount : 0;

    if (totalAdditionalCharge > 0) {
      if (imprintAreaKey !== appliedKeyInOrder[0]) {
        let calResult = totalAdditionalCharge / (appliedKeyInOrder.length - 1);
        _charge += calResult;
      }
    }
  }

  return _charge > 0 && formatCharge(_charge);
};

export const hasAdditionalChargeMoney = (state, formatKey) => {
  const imprintAreaDict = state.imprintAreaDict;

  if (imprintAreaDict[formatKey].isInsideCover) {
    const insideCover = getInsideCoverCharge(state);
    const additionalCharge = insideCover.hasCharge && !insideCover.isNotAvailable ? insideCover.chargeAmount : 0;

    if (additionalCharge > 0) {
      return true;
    }
  }

  if (imprintAreaDict[formatKey].isSuperSized) {
    const logoEnhancement = getLogoEnhancementCharge(state);
    const additionalCharge =
      logoEnhancement.hasCharge && !logoEnhancement.isNotAvailable ? logoEnhancement.chargeAmount : 0;
    if (additionalCharge > 0) {
      return true;
    }
  }

  const currentImprintAreaKey = getCurrentImprintAreaKey(state);
  const currentImprintAreaDict = getAllNextGenUIImprintAreaDict(state);
  const currentGroup = currentImprintAreaDict[currentImprintAreaKey].group;
  const combinations = state.imprintAreasCombinedDict[currentGroup].combinations;

  const allImprintKeys = getAllNextGenUIImprintAreaKeys(state);
  const availableCombinations = combinations.filter(
    (c) => c.formats.length > 1 && c.formats.indexOf(currentImprintAreaKey) > -1
  );

  let hasAdditionalCharge = false;

  availableCombinations.forEach((c) => {
    const formateKeys = allImprintKeys.filter((f) => c.formats.indexOf(f) > -1);

    const formatKeyIndex = formateKeys.indexOf(formatKey);
    if (formatKeyIndex > 0) {
      const additionalImprint = getAdditionalImprintCharge(state, formateKeys.length);
      const totalAdditionalCharge =
        additionalImprint.hasCharge && !additionalImprint.isNotAvailable ? additionalImprint.chargeAmount : 0;
      if (totalAdditionalCharge > 0) {
        hasAdditionalCharge = true;
        return;
      }
    }
  });

  return hasAdditionalCharge;
};

export const getAppliedRuleInGroup = (state, groupName) => {
  const group = state.ui.imprintGroups[groupName];
  const allAppliedKeys = getAllAppliedImprintAreaKeys(state);
  let appliedRule = {};

  Object.values(group.imprintRules).forEach((r) => {
    r.imprintAreas.forEach((imprintArea) => {
      if (allAppliedKeys.indexOf(imprintArea) > -1) {
        appliedRule = r;
      }
    });
  });

  return appliedRule;
};

export const getSelectedRuleInGroup = (state, groupName) => {
  const group = state.ui.imprintGroups[groupName];
  const allSelectedKeys = getSelectedImprintAreaKeys(state);
  let appliedRule = {};

  Object.values(group.imprintRules).forEach((r) => {
    r.imprintAreas.forEach((imprintArea) => {
      if (allSelectedKeys.indexOf(imprintArea) > -1) {
        appliedRule = r;
      }
    });
  });

  return appliedRule;
};

export const getImprintThumbnail = (state, imprintAreakey) => {
  const allImprintKeys = getAllSelectedImprintAreaKeys(state);
  const isSelected = allImprintKeys.indexOf(imprintAreakey) > -1; //getImprintAreaApplied(state, imprintAreakey);

  const customized = getImprintAreaCustomized(state, imprintAreakey);
  let thumbUrl = '';
  if (isSelected && customized) {
    const previews = getProductPreviews(state, imprintAreakey);
    if (previews && previews.thumbnailUrl && previews.thumbnailUrl.length > 0) {
      let preview = previews.thumbnailUrl.find((thumbnailUrl) => thumbnailUrl.isDefault && thumbnailUrl.isFullVignette);
      if (!preview) {
        preview = previews.thumbnailUrl.find((thumbnailUrl) => thumbnailUrl.isFullVignette);
      }

      if (!preview) {
        preview = previews.thumbnailUrl.find((thumbnailUrl) => thumbnailUrl.isDefault);
      }

      if (!preview) {
        preview = previews.thumbnailUrl.find((thumbnailUrl) => !thumbnailUrl.isDefault && !thumbnailUrl.isFullVignette);
      }

      if (preview) {
        thumbUrl = preview.url;
      }
    }
  }

  if (thumbUrl) {
    return thumbUrl;
  }

  const thumbDict = state.imprintThumbnailsDict;
  if (Object.keys(thumbDict).includes(imprintAreakey)) {
    thumbUrl = thumbDict[imprintAreakey];
  }

  if (thumbUrl) {
    const previewColor = getPreviewColor(state);

    let preview = { url: thumbUrl };
    thumbUrl = replaceDesignPreviewUrlPlaceholder(preview, previewColor);

    const bomColorsInfo = getBomColorsInfo(state);
    if (bomColorsInfo) {
      if (bomColorsInfo.bomOptionType === bomOptionType.Multiple) {
        const questions = state.questions;
        const bomColors = Object.keys(bomColorsInfo.bomColors);
        const colorCodes = getColorCode(state);

        const hasMultiple = questions.some(
          (c) => colorCodes.indexOf(c.code) > -1 && c.selectionType === SelectionType.MULTIPLE
        );

        if (!hasMultiple) {
          bomColors.forEach((code) => {
            const bomColorInfo = bomColorsInfo.bomColors[code];
            thumbUrl = replaceColorPlaceholder(thumbUrl, bomColorInfo, state);
          });
        } else {
          colorCodes.forEach((code) => {
            const isMultiple = questions.find((x) => x.code === code)?.selectionType === SelectionType.MULTIPLE;

            if (isMultiple) {
              const bomColorInfo = getCurrentBomOption(state);
              thumbUrl = replaceColorPlaceholder(thumbUrl, bomColorInfo, state);
            } else {
              const bomColorInfo = bomColorsInfo.bomColors.find((color) => color.code === code);
              thumbUrl = replaceColorPlaceholder(thumbUrl, bomColorInfo, state);
            }
          });
        }
      } else {
        const bomColorInfo = getCurrentBomOption(state);
        thumbUrl = replaceColorPlaceholder(thumbUrl, bomColorInfo, state);
      }
    }
  } else {
    const thumbnailFilename = getImprintStaticThumbnailImage(state, imprintAreakey);
    return `https://images.promotionalstore.com/is/image/PromotionalStore/i?src=is{PromotionalStore/imprintthumb_${thumbnailFilename}}&$Studio_ImprintThumb$`;
  }

  return thumbUrl;
};

const replaceColorPlaceholder = (thumbUrl, bomColorInfo, state) => {
  if (bomColorInfo && thumbUrl) {
    const placeholder = `(_.-!${bomColorInfo.code}!-._)`;
    let colorParam = '';
    if (bomColorInfo.useTexture) {
      const selectedTextureColor = getTexturizedImageName(state, bomColorInfo);
      colorParam = `src=is(PromotionalStore/${selectedTextureColor})&res=10`;
    } else {
      let colorReference = bomColorInfo.colorReference;
      if (colorReference.indexOf('#') === 0) {
        colorReference = colorReference.substring(1);
      }

      colorParam = `color=${colorReference}`;
    }
    thumbUrl = thumbUrl.replace(placeholder, colorParam);
  }

  return thumbUrl;
};

export const getAllGroupImprintAreas = (state) => {
  const groups = state.ui.imprintGroups;

  let groupDict = {};
  Object.values(groups).forEach((g) => {
    let groupImprintAreas = [];
    const rules = g.imprintRules;

    Object.values(rules).forEach((r) => {
      groupImprintAreas = [...groupImprintAreas, ...r.imprintAreas];
    });

    groupImprintAreas = [...groupImprintAreas, ...g.gImprintAreas];

    groupDict[g.name] = groupImprintAreas;
  });

  return groupDict;
};

export const getPreviewImprintAreas = (state) => {
  const currentImprintAreaKey = getCurrentImprintAreaKey(state);
  let groupDict = {};
  const groups = state.ui.imprintGroups;

  Object.values(groups).forEach((g) => {
    let groupImprintAreas = [];
    const rules = g.imprintRules;

    Object.values(rules).forEach((r) => {
      //groupImprintAreas = [...groupImprintAreas, ...r.imprintAreas];
      if (r.imprintAreas.indexOf(currentImprintAreaKey) > -1) {
        groupImprintAreas = [...groupImprintAreas, ...r.imprintAreas];
      } else {
        let ruleApplied = false;
        r.imprintAreas.forEach((imprintKey) => {
          const applied = getImprintAreaApplied(state, imprintKey);
          if (applied) {
            ruleApplied = true;
          }
        });

        if (ruleApplied) {
          groupImprintAreas = [...groupImprintAreas, ...r.imprintAreas];
        }
      }
    });

    if (groupImprintAreas.length > 0 || (g.gImprintAreas && g.gImprintAreas.indexOf(currentImprintAreaKey) > -1)) {
      groupImprintAreas = [...groupImprintAreas, ...g.gImprintAreas];
    }

    if (groupImprintAreas.length > 0) {
      groupDict[g.name] = groupImprintAreas;
    }
  });

  return groupDict;
};

export const getAllNextGenUIImprintAreaKeys = (state) => {
  let keys = [];

  const groupAreas = getAllGroupImprintAreas(state);
  const areas = Object.values(groupAreas);
  areas.forEach((formatArea) => {
    keys = [...keys, ...formatArea];
  });

  return keys;
};

export const getAllNextGenUIImprintAreaDict = (state) => {
  const groups = state.ui.imprintGroups;

  let imprintAreaDict = {};
  let areaIndex = 0;

  Object.values(groups).forEach((g) => {
    Object.values(g.imprintRules).forEach((r) => {
      if (r.imprintAreas && r.imprintAreas.length > 0) {
        r.imprintAreas.forEach((a) => {
          let areaInfo = { key: a, index: areaIndex, rule: r.ruleName, group: g.name };

          areaIndex++;
          imprintAreaDict[a] = areaInfo;
        });
      }
    });

    g.gImprintAreas.forEach((a) => {
      let areaInfo = { key: a, index: areaIndex, rule: '', group: g.name };
      areaIndex++;
      imprintAreaDict[a] = areaInfo;
    });
  });

  return imprintAreaDict;
};

/**
 * only use for ImprintOption Assemble （Popup 1b in old studio）
 * excluding NA-NA-NA and irrespective of NoImprint
 */
export const getTempSelectedImprintAreaKeys = createSelector(getAllSelectedImprintAreaKeys, (selectedKeys) => {
  const selectedKeysTemp = selectedKeys.filter((format) => {
    return format !== IMPRINT_AREA_NONE;
  });
  return selectedKeysTemp;
});

/**
 * only use for ImprintOption Assemble （Popup 1b in old studio）
 * excluding NA-NA-NA and irrespective of NoImprint
 */
export const getTempSelectedImprintAreas = createSelector(
  getTempSelectedImprintAreaKeys,
  (state) => state.imprintAreaDict,
  (imprintAreasSelected, imprintAreaDict) => {
    let chosen = [];
    imprintAreasSelected.map((format, _) => {
      if (imprintAreaDict[format]) {
        chosen = chosen.concat(imprintAreaDict[format]);
      }
      return true;
    });
    return chosen;
  }
);

/**
 * all imprint areas key excluding NA-NA-NA and excluding NoImprint for (ScreenPrinted)Digital
 */
export const getSelectedImprintAreaKeys = createSelector(
  getAllSelectedImprintAreaKeys,
  (state) => state.specs.studioItemType,
  (state) => state.imprintOptionTypeDict,
  (selectedKeys, studioType, imprintOptionTypeDict) => {
    const selectedKeysWithoutNaNoImprint = selectedKeys.filter((formatkey) => {
      return (
        formatkey !== IMPRINT_AREA_NONE &&
        (studioType === studioItemType.digital ||
          (() => {
            const optionType = getImprintOptionTypeHelper(imprintOptionTypeDict[formatkey]);
            return optionType !== imprintOptionType.NoImprint && optionType !== imprintOptionType.NA;
          })())
      );
    });
    return selectedKeysWithoutNaNoImprint;
  }
);

/**
 * all imprint format excluding NA-NA-NA
 */
export const getSelectedImprintAreas = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state.imprintAreaDict,
  (imprintAreasSelected, imprintAreaDict) => {
    let chosen = [];
    imprintAreasSelected.map((format, _) => {
      if (imprintAreaDict[format]) {
        chosen = chosen.concat(imprintAreaDict[format]);
      }
      return true;
    });
    return chosen;
  }
);

export const getSelectedImprintLocation = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state.imprintLocationDict,
  (imprintAreasSelected, imprintLocationDict) => {
    const selectedImprintLocation = {};
    imprintAreasSelected.forEach((imprintAreaKey, _) => {
      if (imprintLocationDict[imprintAreaKey]) {
        const imprintLocation = imprintLocationDict[imprintAreaKey].find((x) => x.selected);
        if (imprintLocation) {
          selectedImprintLocation[imprintAreaKey] = imprintLocation.locationCode;
        }
      }
    });

    return selectedImprintLocation;
  }
);

export const getSelectedLogoLocation = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state.logoLocationDict,
  (state) => state,
  (imprintAreasSelected, logoLocationDict, state) => {
    const selectedLogoLocation = {};
    imprintAreasSelected.forEach((formatkey, _) => {
      const optionType = getImprintOptionType(state, formatkey);
      if (optionType === imprintOptionType.TextOnly) {
        selectedLogoLocation[formatkey] = TYPESETTING_LOCATION_NONE;
      } else if (optionType === imprintOptionType.LogoOnly) {
        selectedLogoLocation[formatkey] = TYPESETTING_LOCATION_LOGO;
      } else if (optionType === imprintOptionType.TextLogo) {
        if (logoLocationDict[formatkey]) {
          const imprintLocation = logoLocationDict[formatkey].find((x) => x.selected);
          if (imprintLocation) {
            selectedLogoLocation[formatkey] = imprintLocation.locationCode;
          }
        }
      } else {
        selectedLogoLocation[formatkey] = undefined;
      }
    });

    return selectedLogoLocation;
  }
);

export const getCurrentImprintFormat = createSelector(
  (state) => state.imprintAreaDict,
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  (imprintAreaDict, currentKey) => (currentKey && imprintAreaDict[currentKey] ? imprintAreaDict[currentKey] : null)
);

export const getCurrentTextLayout = createSelector(
  getSelectedImprintLocation,
  getCurrentImprintAreaKey,
  (selectedImprintLocation, currentKey) =>
    currentKey && selectedImprintLocation[currentKey] ? selectedImprintLocation[currentKey] : ''
);

export const getCurrentLogoLayout = createSelector(
  getSelectedLogoLocation,
  getCurrentImprintAreaKey,
  (selectedLogoLocation, currentKey) =>
    currentKey && selectedLogoLocation[currentKey] ? selectedLogoLocation[currentKey] : ''
);
export const hasMissingInMultipleGroups = createSelector(
  (state) => state.imprintAreasCombinedDict,
  (imprintAreasCombinedDict) => {
    let allGroupKeys = Object.keys(imprintAreasCombinedDict);
    const missingChosen =
      allGroupKeys.length > 1 &&
      allGroupKeys.find((x) => imprintAreasCombinedDict[x].combinations.every((y) => !y.selected));
    return missingChosen;
  }
);

export const isImprintAreaNoneSelected = createSelector(
  getAllSelectedImprintAreaKeys, //including NA-NA-NA
  hasMissingInMultipleGroups,
  (selectedImprintAreas, hasMissingInMultipleGroups) =>
    !hasMissingInMultipleGroups &&
    selectedImprintAreas.length > 0 &&
    selectedImprintAreas.every((x) => x === IMPRINT_AREA_NONE)
);

export const hasOnlyOneImprintArea = createSelector(
  (state) => state.imprintAreasCombinedDict,
  (imprintAreasCombinedDict) =>
    imprintAreasCombinedDict &&
    Object.keys(imprintAreasCombinedDict).length === 1 &&
    Object.keys(imprintAreasCombinedDict).every(
      (gkey) =>
        imprintAreasCombinedDict[gkey].combinations.filter(
          (com) => !(com.formats && com.formats.every((x) => x === IMPRINT_AREA_NONE))
        ).length === 1
    )
);

export const getHasOnlyOneCombinationAndNotNoImprint = createSelector(
  (state) => state.imprintAreasCombinedDict,
  (imprintAreasCombinedDict) =>
    imprintAreasCombinedDict &&
    Object.keys(imprintAreasCombinedDict).every(
      (gkey) =>
        imprintAreasCombinedDict[gkey].combinations.length === 1 &&
        imprintAreasCombinedDict[gkey].combinations[0].formats.every((x) => x !== IMPRINT_AREA_NONE)
    )
);

const getUnitializedImprintAreaKeys = createSelector(
  getInitializedImprintAreaKeys,
  getSelectedImprintAreaKeys,
  (initializedImprintAreaKeys, selectedImprintAreaKeys) => {
    let uninitializedImprintAreaKeys = [];
    for (let selectImprintAreaKey of selectedImprintAreaKeys) {
      if (initializedImprintAreaKeys.indexOf(selectImprintAreaKey) === -1) {
        uninitializedImprintAreaKeys.push(selectImprintAreaKey);
      }
    }
    return uninitializedImprintAreaKeys;
  }
);

export const getUninitializedImprintAreas = createSelector(
  getUnitializedImprintAreaKeys,
  (state) => state.imprintAreaDict,
  (unitializedImprintAreaKeys, imprintAreaDict) => {
    let chosen = [];
    unitializedImprintAreaKeys.map((format, _) => {
      if (imprintAreaDict[format]) {
        chosen = chosen.concat(imprintAreaDict[format]);
      }
      return true;
    });
    return chosen;
  }
);

export const getUninitializedImprintLocation = createSelector(
  getUnitializedImprintAreaKeys,
  getSelectedImprintLocation,
  (unitializedImprintAreaKeys, selectedImprintLocationDict) => {
    let uninitializedImprintLocation = {};
    unitializedImprintAreaKeys.map((imprintAreaKey, _) => {
      if (selectedImprintLocationDict[imprintAreaKey]) {
        const imprintLocationCode = selectedImprintLocationDict[imprintAreaKey];
        if (imprintLocationCode) {
          uninitializedImprintLocation[imprintAreaKey] = imprintLocationCode;
        }
      }
      return true;
    });
    return uninitializedImprintLocation;
  }
);

export const getUninitializedLogoLocation = createSelector(
  getUnitializedImprintAreaKeys,
  getSelectedLogoLocation,
  (unitializedImprintAreaKeys, selectedLogoLocationDict) => {
    let uninitializedLogoLocation = {};
    unitializedImprintAreaKeys.map((imprintAreaKey, _) => {
      if (selectedLogoLocationDict[imprintAreaKey]) {
        let logoLocationCode = selectedLogoLocationDict[imprintAreaKey];
        if (logoLocationCode) {
          uninitializedLogoLocation[imprintAreaKey] = logoLocationCode;
        }
      }
      return true;
    });
    return uninitializedLogoLocation;
  }
);

export const getImprintFormatDetail = createSelector(
  (state) => state.imprintAreaDict,
  getSelectedImprintLocation,
  getSelectedLogoLocation,
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  (imprintAreaDict, imprintLocationSelected, logoLayoutSelected, formatKey) => {
    const format = imprintAreaDict[formatKey];
    const imprintLocation = imprintLocationSelected[formatKey] || '';
    const logoLocation = logoLayoutSelected[formatKey] || '';
    return { format, imprintLocation, logoLocation };
  }
);

/**
 * to create ImprintOption choose swatch, 'Text Only', 'Logo/Art Only', 'Logo/Art & Text', 'No Imprint',
 */
export const getImprintOptionTypeLayout = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.imprintOptionTypeDict,
  (currentKey, imprintOptionTypeDict) => {
    return imprintOptionTypeDict[currentKey] || [];
  }
);

export const hasTextImprintOptionType = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state,
  (selectedImprintAreaKeys, state) => {
    let hasTextImprintOptionType = false;
    selectedImprintAreaKeys.forEach((selectedImprintAreaKey, _) => {
      const optionType = getImprintOptionType(state, selectedImprintAreaKey);
      if (optionType === imprintOptionType.TextOnly || optionType === imprintOptionType.TextLogo) {
        hasTextImprintOptionType = true;
        return hasTextImprintOptionType;
      }
    });
    return hasTextImprintOptionType;
  }
);

/**
 * to create Logo layout choose swatch
 */
export const getLogoLayoutInitialization = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.logoLocationDict,
  getImprintOptionTypeLayout,
  (state) => state,
  (currentKey, logoLocationDict, imprintOptionTypeLayout, state) => {
    let logoLayoutList = locationListSort(logoLocationDict[currentKey] || []).map((location) => ({
      ...layoutFormat(location),
      icon: `/resources/assets/imprint/layout/${location.locationCode}.svg`,
    }));
    const isHideOptionTypeSelectionForLaserGrow = getIsHideOptionTypeSelectionForLaserGrow(state, currentKey);
    if (isHideOptionTypeSelectionForLaserGrow) {
      logoLayoutList = [
        ...logoLayoutList,
        Object.assign(
          {},
          imprintOptionTypeLayout.find((x) => x.key === imprintOptionType.LogoOnly),
          { icon: '/resources/assets/imprint/layout/LogoOnly.svg' }
        ),
      ];
    }
    return logoLayoutList;
  }
);

/**
 * to create Text layout (Imprint Location) choose swatch
 */
export const getTextLayoutInitialization = createSelector(
  getCurrentImprintAreaKey,
  (state) => state.imprintLocationDict,
  (state) => state.ui.imprintOption,
  (currentKey, imprintLocationDict) => {
    return locationListSort(imprintLocationDict[currentKey] || []).map(layoutFormat);
  }
);

export const getCurrentImprintOptionUI = createSelector(
  (_, formatkey) => formatkey,
  (state) => state.ui.imprintOption,
  (state) => state.imprintAreaDict,
  (formatkey, imprintOptionUI, imprintAreas) => {
    const optionUI = imprintOptionUI[formatkey] || {};
    const imprintArea = imprintAreas[formatkey] || {};
    return Object.assign({}, { ...optionUI }, { sloganAvailable: imprintArea.sloganAvailable });
  }
);

export const getAllImprintOptionUI = createSelector(
  (state) => state.ui.imprintOption,
  (imprintOption) => imprintOption
);

/**
 * for ImprintColor Grid
 */
export const getImprintColors = createSelector(
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  (state) => state.imprintColorDict,
  (state) => state.imprintAreaDict,
  getMatchTrimColor,
  (formatKey, imprintColorDict, imprintAreas, bomTrimColor) => {
    let clrlst = [];
    if (imprintAreas[formatKey] && imprintAreas[formatKey].imprintAreaCode) {
      clrlst = imprintColorFormat(imprintColorDict[imprintAreas[formatKey].imprintAreaCode] || [], bomTrimColor);
    }
    return clrlst;
  }
);

export const getDebossColorReference = createSelector(
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  (state) => state.imprintColorDict,
  (state) => state.imprintAreaDict,
  getMatchTrimColor,
  (formatKey, imprintColorDict, imprintAreas, bomTrimColor) => {
    let clrlst = [];
    if (imprintAreas[formatKey] && imprintAreas[formatKey].imprintAreaCode) {
      clrlst = imprintColorFormat(imprintColorDict[imprintAreas[formatKey].imprintAreaCode] || [], bomTrimColor);

      const debossColor = clrlst.find((x) => x.colorId === 'DEB');
      return debossColor?.colorReference;
    }
    return '';
  }
);

export const getHasOneImprintColorOnly = createSelector(
  (state) => state.specs.studioItemType,
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  getImprintColors,
  (state) => state.imprintAreaDict,
  (studioType, formatKey, imprintColorList, imprintArea) => {
    return (
      studioType !== studioItemType.digital &&
      imprintArea &&
      imprintArea[formatKey] &&
      imprintArea[formatKey].maxColorCount === 1 &&
      imprintColorList.length === 1
    );
  }
);

export const getIsHideOptionTypeSelectionForLaserGrow = createSelector(
  (state) => state,
  (_, formatKey) => formatKey,
  (state, formatKey) => {
    const imprintAreaDict = state.imprintAreaDict;
    const logoLocationDict = state.logoLocationDict;
    const currentStudioItemType = getStudioItemType(state);

    return (
      currentStudioItemType === studioItemType.nonDigital &&
      Object.keys(imprintAreaDict).length > 1 &&
      imprintAreaDict[formatKey] &&
      !imprintAreaDict[formatKey].logoOnly &&
      getIsLaserGrowType(state, formatKey) &&
      logoLocationDict[formatKey] &&
      logoLocationDict[formatKey].length > 0
    );
  }
);

export const getImprintOptionOfferLogo = createSelector(
  (state) => state,
  (_, formatKey) => formatKey,
  (state, formatKey) => {
    return checkImprintOptionOfferLogo(state, formatKey);
  }
);

/**
 * ImprintOptionType.TextOnly, LogoOnly, TextAndLogo, or NoImprint choose in popup 1b
 */
export const getImprintOptionType = createSelector(
  (state, formatKey) => state.imprintOptionTypeDict[formatKey],
  (imprintOptionTypeArr) => {
    return getImprintOptionTypeHelper(imprintOptionTypeArr);
  }
);

export const getImprintOptionOfferSlogan = createSelector(
  (state, formatKey) => state.imprintAreaDict[formatKey],
  (imprintformat) => imprintformat && imprintformat.sloganAvailable
);

export const getImprintOptionOfferOutSideSlogan = createSelector(
  (state, imprintAreaCode) => state.sloganDict[imprintAreaCode],
  (sloganDict) => {
    const hasOutsideSlogan =
      sloganDict && typeof sloganDict.slogans !== 'undefined' && sloganDict.slogans.some((x) => x.isOutside);
    return hasOutsideSlogan;
  }
);

/**
 * for maxColorCount check
 */
export const getSelectedImprintColors = createSelector(
  (state, currentFormatKey) => state.ui.imprintOption[currentFormatKey],
  (state, currentFormatKey) => state.textDict[currentFormatKey],
  (state, currentFormatKey) => state.imageDict[currentFormatKey],
  (_0, _1, hasSpecialImage) => hasSpecialImage,
  (_0, _1, _2, notIncludeCurrentImage) => notIncludeCurrentImage,
  (imprintOption, texts, images, hasSpecialImage, notIncludeCurrentImage) => {
    let colorArr = [];

    if (texts && texts.length > 0 && texts[0].data && texts[0].data.color) {
      texts.forEach((text) => {
        if (!colorArr.some((x) => x.colorId === text.data.color.colorId)) {
          colorArr = [...colorArr, text.data.color];
        }

        if (
          text.data.outlineThickness !== outlineThicknessOption.None &&
          !colorArr.some((x) => x.colorId === text.data.outlineColor.colorId)
        ) {
          colorArr = [...colorArr, text.data.outlineColor];
        }
      });
    }
    //let pickImages = hasSpecialImage ? images : getNormalImages(images);
    let pickImages = images;

    if (notIncludeCurrentImage) {
      pickImages = pickImages.filter((i) => i.ui.status !== elementStatus.SingleSelected);
    }

    if (pickImages && pickImages.length > 0) {
      pickImages.forEach((image) => {
        if (image && image.data && image.data.colors && image.data.colors.length > 0) {
          colorArr = [...colorArr, ...image.data.colors];
        }
      });
    }

    const colorsPicked = removeColorDuplicates(colorArr);
    return colorsPicked;
  }
);

export const getOneTextLayoutOnly = createSelector(
  (_, formatKey) => formatKey,
  (state) => state.imprintLocationDict,
  (formatKey, imprintLocation) => {
    return !imprintLocation[formatKey] || imprintLocation[formatKey].length <= 1;
  }
);

export const getOneLogoLayoutOnly = createSelector(
  (_, formatKey) => formatKey,
  (state) => state.logoLocationDict,
  (formatKey, logoLocation) => {
    return !logoLocation[formatKey] || logoLocation[formatKey].length <= 1;
  }
);

export const getMakeSelectionAutoCheck = createSelector(
  (_, formatKey) => formatKey,
  (state) => state,
  (formatKey, state) => {
    const imprintArae = state.imprintAreaDict[formatKey];
    const offerLogo = getImprintOptionOfferLogo(state, formatKey);
    const textOnly = !offerLogo;
    const logoOnly = offerLogo && imprintArae && imprintArae.logoOnly;
    const oneTextLayoutOnly = getOneTextLayoutOnly(state, formatKey);
    const oneLogoLayoutOnly = getOneLogoLayoutOnly(state, formatKey);
    const oneImprintColorOnly = getHasOneImprintColorOnly(state, formatKey);
    return (textOnly || logoOnly) && oneTextLayoutOnly && oneLogoLayoutOnly && oneImprintColorOnly;
  }
);

//the lightest imprint color for bom option
export const getDefaultImprintColor = createSelector(
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (state) => state.ui.previewDefaultImprintColorDict,
  (imprintAreaKey, previewDefaultImprintColorDict) => previewDefaultImprintColorDict[imprintAreaKey]
);

export const getTraditonalImprintColorForImprintKeys = createSelector(
  getSelectedImprintAreaKeys,
  (state) => state,
  (selectedImprintAreaKeys, state) => {
    const imprintColor = {};
    selectedImprintAreaKeys.forEach((imprintAreaKey) => {
      Object.assign(imprintColor, {
        [imprintAreaKey]: getImprintColors(state, imprintAreaKey).filter(
          (i) => i.colorType === colorType.Traditional && i.colorReference
        ),
      });
    });
    return imprintColor;
  }
);

/**
 * scene7 item, after user image has chosen, apply one default imprint color on it
 * return text.color or imprintOption.textColor or BLK or imprintColorList[0]
 */
export const getDefaultUserImageColor = createSelector(
  (_, formatkey) => formatkey,
  (state) => state,
  (formatkey, state) => {
    let clr = {};
    let isBackup = false;
    const imprintArea = state.imprintAreaDict[formatkey];
    const texts = state.textDict[formatkey];
    if (
      texts &&
      texts.length > 0 &&
      texts[0].data &&
      texts[0].data.color &&
      imprintArea &&
      imprintArea.maxColorCount <= 1
    ) {
      clr = texts[0].data.color;
      isBackup = true;
    } else {
      //const imprintOption = getCurrentImprintOptionUI(state, formatkey);
      // if (imprintOption && imprintOption.textColor) {
      //   clr = imprintOption.textColor;
      //   isBackup = true;
      // } else {
      const images = state.imageDict[formatkey];

      const allColorsInUse = getSelectedImprintColors(state, formatkey);
      if (
        images &&
        images.length > 0 &&
        images[0].data &&
        images[0].data.colors &&
        images[0].data.colors.length > 0 &&
        imprintArea &&
        (imprintArea.maxColorCount <= 1 || imprintArea.maxColorCount <= allColorsInUse.length)
      ) {
        clr = images[0].data.colors[0];
      } else {
        clr = getDefaultImprintColor(state, formatkey);
      }
      // }
    }
    return [clr, isBackup];
  }
);

export const getImprintOptionMaxColorCount = createSelector(
  (state) => state.imprintAreaDict,
  (state, formatkey) => formatkey || getCurrentImprintAreaKey(state),
  (imprintAreaDict, currentKey) =>
    currentKey && imprintAreaDict[currentKey] && imprintAreaDict[currentKey].maxColorCount
      ? imprintAreaDict[currentKey].maxColorCount
      : 1
);

/**
 * getImprintOptionLogoTypeCheck(state, currentFormatLey)
 * @param {state}
 * @param {currentFormatLey}
 * always return true for (ScreenPrinted)Digital Studio
 */
export const getImprintOptionLogoTypeCheck = createSelector(
  getImprintOptionType,
  (state) => state.specs.studioItemType,
  (optionType, studioType) =>
    studioType !== studioItemType.nonDigital ||
    optionType === imprintOptionType.LogoOnly ||
    optionType === imprintOptionType.TextLogo
);

/**
 * getImprintOptionTextTypeCheck(state, currentFormatLey)
 * @param {state}
 * @param {currentFormatLey}
 * Only Check NonDigital Studio
 */
export const getImprintOptionTextTypeCheck = createSelector(
  getImprintOptionType,
  (state) => state.specs.studioItemType,
  (optionType, studioType) =>
    studioType === studioItemType.nonDigital &&
    (optionType === imprintOptionType.TextOnly || optionType === imprintOptionType.TextLogo)
);

/**
 * for Imprint Text Color Select
 * getImprintOptionMaxColorCount(state, formatkey)
 * getSelectedImprintColors(state, formatkey)
 */
export const getMultipleImageColorsTopToMaxCheck = createSelector(
  (state) => state,
  (_, formatkey) => formatkey,
  (state, formatkey) => {
    const allColors = getSelectedImprintColors(state, formatkey);

    const maxCount = getImprintOptionMaxColorCount(state, formatkey);

    return maxCount > 1 && maxCount <= allColors.length;
  }
);

export const getIlluminatedLaserCheck = createSelector(
  (state) => state.imprintAreaDict[getCurrentImprintAreaKey(state)],
  (imprintarea) => {
    return (
      imprintarea &&
      (imprintarea.processType === ProcessTypes.IlluminatedLaser ||
        imprintarea.processType === ProcessTypes.ColoredIlluminatedLaser)
    );
  }
);

/**
 * PMS Color Special Charge
 */
export const getPMSColorSpecialCharge = createSelector(getPmsColorCharge, (charge) => {
  return (
    charge && charge.hasCharge && !charge.isNotAvailable && charge.chargeAmount > 0 && formatCharge(charge.chargeAmount)
  );
});

/**
 * Multiple Colors Special Charge
 * @param {string} formatkey
 * @param {boolean} hasSpecialImage
 */
export const getMultipleColorsSpecialCharge = createSelector(
  (state, formatkey) => getSelectedImprintColors(state, formatkey || getCurrentImprintAreaKey(state), true),
  getAdditionalColorCharge,
  (imprintColors, charge) => {
    return (
      imprintColors.length > 1 &&
      charge.hasCharge &&
      !charge.isNotAvailable &&
      charge.chargeAmount > 0 &&
      formatCharge(charge.chargeAmount)
    );
  }
);

/**
 * Imprint Area Special Charge
 * @param {Array} formatkeys
 */
export const getImprintAreaSpecialCharge = createSelector(
  (state) => state,
  (_, formatkeys) => formatkeys,
  (state, formatkeys) => {
    const total = getImprintAreaSpecialChargeMoney(state, formatkeys);
    return total > 0 && formatCharge(total);
  }
);

export const getImprintAreaSpecialChargeMoney = createSelector(
  (state) => state,
  (_, formatkeys) => formatkeys,
  (state, formatkeys) => {
    const imprintAreaDict = state.imprintAreaDict;
    const additionalImprint = getAdditionalImprintCharge(state, formatkeys.length);
    const additionalImprintCharge =
      additionalImprint.hasCharge && !additionalImprint.isNotAvailable ? additionalImprint.chargeAmount : 0;
    const insideCoverAndSuperSizedCharge = formatkeys
      .map((key) => {
        let _charge = 0;
        if (imprintAreaDict[key]) {
          if (imprintAreaDict[key].isInsideCover) {
            const insideCover = getInsideCoverCharge(state);
            _charge += insideCover.hasCharge && !insideCover.isNotAvailable ? insideCover.chargeAmount : 0;
          }
          if (imprintAreaDict[key].isSuperSized) {
            const logoEnhancement = getLogoEnhancementCharge(state);
            _charge += logoEnhancement.hasCharge && !logoEnhancement.isNotAvailable ? logoEnhancement.chargeAmount : 0;
          }
        }
        return _charge;
      })
      .reduce((p, c) => p + c, 0);

    return additionalImprintCharge + insideCoverAndSuperSizedCharge;
  }
);

export const getImprintSpecs = createSelector(
  (state) => state,
  (state, formatKey) => formatKey || getCurrentImprintAreaKey(state),
  (state, formatKey) => {
    const studioType = state.specs.studioItemType;
    const canvasData = state.canvasDict[formatKey];

    const access = ((vendor) => {
      return {
        slogan: vendor.allowSlogans,
        upload: vendor.allowLogoUpload,
        stock: vendor.allowStockArt,
        shared: vendor.allowSharedArt,
        saved: vendor.allowSavedArt,
        recent: vendor.allowRecentArt,
      };
    })(state.vendor);

    const vendorallows = Object.values(access).reduce((pre, cur) => {
      return pre || cur;
    }, false);
    const imprintFormat = getCurrentImprintFormat(state, formatKey);
    const offerSlogan = getImprintOptionOfferSlogan(state, formatKey);
    const offerOutSideSlogan = getImprintOptionOfferOutSideSlogan(state, imprintFormat.imprintAreaCode);
    const logoOptionType = getImprintOptionLogoTypeCheck(state, formatKey);
    const textOptionType = getImprintOptionTextTypeCheck(state, formatKey);
    const sloganList = state.sloganDict[getCurrentImprintAreaCode(state)];
    const textLines = getTextLine(formatKey)(state);
    const textLinesAvailable = textLines && textLines[formatKey] && textLines[formatKey].length > 0;
    const digitalTextAvailable =
      state.textDict &&
      state.textDict[formatKey] &&
      state.textDict[formatKey].length &&
      state.textDict[formatKey].length > 0;

    // return {
    //   text:
    //     studioType === studioItemType.screenPrintedDigital ||
    //     (studioType === studioItemType.digital && digitalTextAvailable) ||
    //     (textOptionType && textLinesAvailable),
    //   logo:
    //     studioType !== studioItemType.nonDigital ||
    //     (vendorallows && (logoOptionType || (offerSlogan && sloganList.slogans?.length > 0 && !offerOutSideSlogan))),
    // };

    return {
      text: canvasData.allowText,
      logo: vendorallows && canvasData.allowLogo,
    };
  }
);

export const getVerseAvailable = createSelector(
  getCurrentImprintFormat,
  (state) => state.verseList,
  (currentImprintFormat, verselist) => {
    return currentImprintFormat.verseAvailable && (verselist || []).length > 0;
  }
);
export const getSentimentAvailable = createSelector(
  getCurrentImprintFormat,
  (state) => state.sentimentList,
  (currentImprintFormat, sentimentlist) => {
    return currentImprintFormat.sentimentAvailable && (sentimentlist || []).length > 0;
  }
);

export const getImprintAreaWidthRange = createSelector(
  (state, formatKey) => getCurrentImprintFormat(state, formatKey),
  (format) => {
    if (format) return { minDisplayWidth: format.minDisplayWidth, maxDisplayWidth: format.maxDisplayWidth };

    return null;
  }
);

export const isAllTempSelectedImprintAreaNoImprint = createSelector(
  (state) => state,
  getTempSelectedImprintAreaKeys,
  (state, tempSelectedImprintAreaKeys) => {
    const noImprintChosen = tempSelectedImprintAreaKeys
      .map((key) => getImprintOptionType(state, key) === imprintOptionType.NoImprint && key)
      .filter((x) => x);
    return noImprintChosen.length === tempSelectedImprintAreaKeys.length;
  }
);

export const getUserImageSetupCheck = createSelector(
  (state, formatkey) => getCurrentImprintOptionUI(state, formatkey),
  (optionUI) => {
    return !optionUI || optionUI.userImageSetup;
  }
);

export const getImprintOptionAutoCheck = createSelector(
  (state, formatkey) => ({ state, formatkey }),
  ({ state, formatkey }) => {
    let isAuto = false;
    const optionUI = getCurrentImprintOptionUI(state, formatkey);
    if (!optionUI || optionUI.auto === undefined) {
      isAuto = getMakeSelectionAutoCheck(state, formatkey);
    } else {
      isAuto = optionUI.auto;
    }
    return isAuto;
  }
);

export const checkShowImageSingleColor = createSelector(
  getStudioItemType,
  (_, image) => image,
  getIlluminatedLaserCheck,
  (studioType, image, illuminatedLaserCheck) => {
    if (!image) return false;

    if (
      studioType === studioItemType.screenPrintedDigital &&
      image.type === userImageType.UploadLogo &&
      image.colors &&
      image.colors.length === 1
    ) {
      return true;
    }

    return !(
      (studioType === studioItemType.digital &&
        image.type === userImageType.UploadLogo &&
        (!image.colors || image.colors.length === 0)) ||
      (studioType !== studioItemType.digital &&
        (image.type === userImageType.Sentiment ||
          (image.type === userImageType.UploadLogo && image.colors && image.colors.length > 0) ||
          (image.type === userImageType.Slogan && illuminatedLaserCheck) ||
          (image.type === userImageType.SharedArt && image.colorSpace === colorSpace.MultipleColor) ||
          ((image.type === userImageType.StockArt || image.type === userImageType.SavedArt) &&
            image.colors &&
            image.colors.length > 1)))
    );
  }
);

export const checkShowImprintOptionsPage = createSelector(
  (state) => state,
  getStudioItemType,
  getSelectedImprintAreaKeys,
  hasOnlyOneImprintArea,
  (state, studioType, selectedImprintAreaKeys, onlyOneImprintArea) => {
    const selectedImprintOptionAuto = selectedImprintAreaKeys.every((key) => getImprintOptionAutoCheck(state, key));
    return !(onlyOneImprintArea && (studioType === studioItemType.digital || selectedImprintOptionAuto));
  }
);

export const getOpitonalText = createSelector(
  (state, imprintAreaKey) => imprintAreaKey || getCurrentImprintAreaKey(state),
  (state) => state.imprintAreaDict,
  (imprintAreaKey, imprintFormatDict) => {
    const optionalText = imprintFormatDict[imprintAreaKey]?.optionalText;
    return optionalText;
  }
);

export const getSpecificImprintArea = createSelector((state, formatKey) => {
  return state.imprintAreaDict[formatKey];
});
export const getSpecialImageLabelOfNoAdded = createSelector(
  (state, imprintAreaKey) => getCanvasImageList(state, imprintAreaKey),
  (state) => getImprintOptionOfferOutSideSlogan(state, getCurrentImprintAreaCode(state)),
  getVerseAvailable,
  getSentimentAvailable,
  (imageList, allowOutsideSlogan, verseAvailable, sentimentAvailable) => {
    let label = '';
    if (
      allowOutsideSlogan &&
      !imageList.some((image) => image.data.type === userImageType.Slogan && image.data.isOutside)
    ) {
      label = 'Slogan';
    } else if (verseAvailable && !imageList.some((image) => image.data.type === userImageType.Verse)) {
      label = 'Verse';
    } else if (sentimentAvailable && !imageList.some((image) => image.data.type === userImageType.Sentiment)) {
      label = 'Sentiment';
    }
    return label;
  }
);
export const hasCustomImprintOnCasvas = (state, imprintAreaKey) => {
  const imageDict = state.imageDict;
  const textDict = state.textDict;

  const hasImage =
    imageDict &&
    imageDict[imprintAreaKey] &&
    imageDict[imprintAreaKey].filter(
      (image) =>
        !(image.data.type === userImageType.Slogan && image.data.isOutside) &&
        image.data.type !== userImageType.Verse &&
        image.data.type !== userImageType.Sentiment
    ).length > 0;
  const hasText = textDict && textDict[imprintAreaKey] && textDict[imprintAreaKey].some((x) => x.data.imprintText);

  const finalTemplate = getFinalTemplateForImprintArea(state, imprintAreaKey);
  const hasTemplate = finalTemplate && finalTemplate.templateType !== TemplateType.BLANK;

  return hasImage || hasText || hasTemplate;
};

export const getIsElementAddToEmptySecondSurface = createSelector(
  (state) => state,
  getCurrentImprintAreaKey,
  getAllAppliedImprintAreaKeys,
  (state, currentImprintAreaKey, allAppliedKeys) => {
    let isAddToEmptySurface = false;
    if (allAppliedKeys.length > 1) {
      const imageDict = state.imageDict;
      const textDict = state.textDict;
      const imageCount =
        (imageDict && imageDict[currentImprintAreaKey] && imageDict[currentImprintAreaKey].length) || 0;
      const textCount =
        (textDict &&
          textDict[currentImprintAreaKey] &&
          textDict[currentImprintAreaKey].filter((x) => x.data.imprintText).length) ||
        0;
      isAddToEmptySurface = imageCount + textCount === 1;
    }
    return isAddToEmptySurface;
  }
);
